import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CultureService } from '../culture/culture.service';

/// What joining the newsletter came to (Contact/Subscribe): the server's 1, 3, 2, 4 and anything else.
export type SubscribeResult = 'joined' | 'already' | 'invalid' | 'busy' | 'failed';

/// What a form tells the visitor about each result.
export interface SubscribeNotice {
    ok: boolean;
    title: string;
    text: string;
}

/// An ordinary address, as the server reads one (NewsletterSignup.IsValid): the form only spares the visitor
/// a round trip, the server decides.
const ADDRESS = /^[a-z0-9._%+\-]+@[a-z0-9](?:[a-z0-9\-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9\-]*[a-z0-9])?)*\.[a-z]{2,}$/i;

export function looksLikeEmail(value: string): boolean {
    const address = String(value || '').trim();
    const local = address.split('@')[0];
    return address.length >= 6 && address.length <= 254 && local.length <= 64 && ADDRESS.test(address)
        && !local.startsWith('.') && !local.endsWith('.') && local.indexOf('..') < 0;
}

/// The newsletter list. Both signup forms - the blog sidebar's and the footer's - go through here, so they
/// check, send and answer the same way.
@Injectable({
    providedIn: 'root'
})
export class NewsletterService {
    constructor(private http: HttpClient, private culture: CultureService) { }

    /// Joins the list. The address travels in the body, not the path (a path is written to every access log).
    /// It never errors: a request that fails is 'failed'.
    subscribe(email: string): Observable<SubscribeResult> {
        return this.http.post<number>(environment.apiUrl + '/api/Contact/Subscribe', { Email: String(email || '').trim() }).pipe(
            map((code): SubscribeResult => code === 1 ? 'joined' : code === 3 ? 'already' : code === 2 ? 'invalid' : code === 4 ? 'busy' : 'failed'),
            catchError(() => of('failed' as SubscribeResult)));
    }

    notice(result: SubscribeResult): SubscribeNotice {
        switch (result) {
            case 'joined':
                return { ok: true, title: this.culture.t('newsletter-joined-title', 'Subscription Successful'),
                         text: this.culture.t('newsletter-joined', 'You have successfully subscribed, Thank you.') };
            case 'already':
                return { ok: true, title: this.culture.t('newsletter-already-title', 'Subscriber'),
                         text: this.culture.t('newsletter-already', 'You are already our subscriber, Thank you!') };
            case 'invalid':
                return { ok: false, title: this.culture.t('newsletter-invalid-title', 'Check the address'),
                         text: this.culture.t('newsletter-invalid', 'That does not look like an email address. Please check it and try again.') };
            case 'busy':
                return { ok: false, title: this.culture.t('newsletter-busy-title', 'Too many tries'),
                         text: this.culture.t('newsletter-busy', 'Please wait a few minutes and try again.') };
            default:
                return { ok: false, title: this.culture.t('newsletter-failed-title', 'Unsuccessful'),
                         text: this.culture.t('something-went-wrong', 'Something went wrong, try again!') };
        }
    }
}
